/**
 * Import.
 *
 * @param {Object} params - Object parameter
 * @param {string} params.process - Kind of Import you want
 * @param {int} params.id - Identifier of the Import
 * @param {string} params.status - Status of the Import
 * @param {string} params.dest - URL of the Import
 * @constructor
 */

export default class ImportRequest {
    constructor({
        id,
        process,
        report,
        details,
        url,
        logs,
        status,
        columns,
        created,
        data,
        source,
        summary
    }) {
        this._id = id;
        this._process = process;
        this._report = report;
        this._details = details || {};
        this._url = url;
        this._logs = logs || [];
        this._status = status;
        this._created = created;
        this._data = data;
        this._source = source;
        this._columns = [];
        if (this._details.columns)
            this._columns  = this._details.columns.map(column => new Column(column));
        this._summary = summary || {created: 0, error: 0, invalid: 0};
    }

    getStatus() {
        return String(this._status);
    }

    getID() {
        return this._id;
    }

    getSource() {
        return this._source;
    }

    getCreated() {
        return this._created;
    }

    getDetails(){
        return this._details;
    }

    getColumns(){
        return this._columns;
    }

    isDone() {
        return this._status === "done";
    }

    getLogs(){
        return this._logs;
    }

    getFilenameWithExtension() {
        return this._source.split('/').pop();
    }

    getFilename() {
        return this._source.split('/').pop().split('.')[0];
    }

    getExtension() {
        const file = this._source.split('.');
        if (file.length > 1) {
            return file.pop();
        }
        return null;
    }

    isValidating(){
        return (this._status === "new" || this._status === "in-validation" );
    }

    isConfirmable(){
        return (this._status === "valid" || this._status === "not-valid" );
    }

    countTotalRows() {
        return this.summaryIsSet() ?
            this.getCreatedSummary() + this.getInvalidSummary()
            : this.countCreatedRows() + this.countErrorRows();
    }

    countCreatedRows() {
        return this._logs
            .filter(({ type }) => type === 'created')
            .reduce((count, { group, line }) => count + 1 + (line - group || 0), 0);
    }

    getData() {
        return this._data;
    }

    countErrorRows() {
        return this._logs
            .filter(({ type }) => type === 'invalid' || type === 'error')
            .reduce((count, { group, line }) => count + 1 + (line - group || 0), 0);
        // line - group will be NaN if group is undefined
        // line is the last line of the group
        // group is the first line of the group
        // line count = group ? line - group + 1 : 1
    }

    getErrorCodeAndField({ code, field, message }) {
        if (!code || !field) {
            return message;
        }
        return `${code}-${field}`;
    }

    getErrorGroups() {
        return this._logs
            .filter(({ type }) => type === "invalid")
            .reduce((errorGroups, { error, group, line }) => {
                error.forEach(errorInstance => {
                    const message = this.getErrorCodeAndField(errorInstance);
                    const errorGroup = errorGroups[message] = errorGroups[message] || [];
                    const errorPosition = (group !== undefined && errorInstance.code === 'replica') ? group : line;
                    errorGroup.push(errorPosition);
                });
                return errorGroups;
            }, {});
    }

    getCreatedSummary() {
        return this.summaryIsSet() ? this._summary.created : this.countCreatedRows();
    }

    getErrorSummary() {
        return this._summary.error;
    }

    getInvalidSummary() {
        return this.summaryIsSet() ? this._summary.invalid : this.countErrorRows();
    }

    summaryIsSet() {
        return !(this._summary.created === 0
            && this._summary.error === 0
            && this._summary.invalid === 0);
    }
}

export class Column {
    constructor({
        target,
        header,
    }) {
        this._target = target;
        this._header = header;
    }

    getTarget() {
        return this._target || "none";
    }

    getHeader() {
        return this._header || "none";
    }
}